/* ───────────────────────────────────────────────────────────────────────────
 * Java Designer — the Java DSL counterpart to the YAML (Kaoto) Designer.
 *
 * Why this exists: the YAML Designer can only edit integrations that carry a
 * `camelRoute`. On this platform that is a minority — 5 of 12 at the time of
 * writing — so the other 7, including most of what runs in production, had no
 * designer at all and were invisible in the one we had.
 *
 * It is a page, not an iframe. Kaoto needs its own bundle and a ~20 MB catalog,
 * which is what earned it a separate Vite build; a Java editor needs Monaco,
 * which the SPA already loads (MonacoLoader.js). So this lives in the SPA and
 * talks to the platform over plain REST.
 *
 * The backend was already complete — this file adds no server code:
 *   GET /api/integrations/source/{id}  -> {id, builderClassName, source}
 *       Falls back to the .java shipped inside the JAR (pom.xml packages
 *       com/taxlab/einvoice/routes/**.java as a resource for exactly this),
 *       so an integration that has never been edited still opens on real code.
 *   PUT /api/integrations/source/{id}  (text/plain)
 *       COMPILES before it persists, then hot-rebuilds the route if the
 *       integration is ACTIVE. {saved, valid, error?, hotReloaded, rebuildError?}
 *
 * The shape deliberately mirrors kaoto-host/src/main.tsx — same picker on the
 * left, same header with a lifecycle badge and a dirty marker, same
 * save-before-activate rule — so moving between the two designers does not mean
 * learning two products.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Java DSL mode is `builderClassName` set; YAML mode is `camelRoute`. The
 *  engine's startJavaRoute() requires builderClassName and refuses without it,
 *  so that — not "has no camelRoute" — is what makes a route editable here. */
function isJavaRoute(i){ return !!(i && (i.builderClassName||'').trim()); }

function JavaDesignerPage({showToast}){
  const [integrations,setIntegrations]=React.useState(null);
  const [openId,setOpenId]=React.useState(null);
  const [className,setClassName]=React.useState('');
  const [dirty,setDirty]=React.useState(false);
  const [isRunning,setIsRunning]=React.useState(false);
  const [status,setStatus]=React.useState({kind:'idle'});

  // Mirrors `dirty` so toggleRunning can read it immediately after awaiting
  // save(). React state is not settled synchronously, and activating on a
  // rejected save would start the PREVIOUS version of the route.
  const dirtyRef=React.useRef(false);
  const markDirty=React.useCallback(v=>{dirtyRef.current=v;setDirty(v);},[]);

  const hostRef=React.useRef(null);
  const edRef=React.useRef(null);
  const valueRef=React.useRef('');
  // Source fetched before Monaco finished mounting. Without this the first
  // route opened on a cold page load silently shows an empty editor.
  const pendingRef=React.useRef(null);

  React.useEffect(()=>{
    API('GET','/api/integrations')
      .then(list=>setIntegrations(Array.isArray(list)?list:[]))
      .catch(e=>{ if(!e.authRequired) setStatus({kind:'error',text:String(e.message||e)}); });
  },[]);

  /* Monaco is mounted once for the page and reused across routes — setValue,
     not a remount. Disposing and recreating per selection drops undo history
     and costs a layout pass for nothing. */
  React.useEffect(()=>{
    if(!hostRef.current) return;
    let cancelled=false;
    if(!window.MonacoAPI){
      setStatus({kind:'error',text:'Monaco editor failed to load — check the CDN is reachable.'});
      return;
    }
    window.MonacoAPI.createEditor(hostRef.current,{
      value:pendingRef.current||'', language:'java', theme:'axon-dark', readOnly:false,
      // MonacoLoader defaults this to false, and the editor is created while its
      // container is still display:none (no route picked yet). Monaco measures
      // once at construction, gets 0x0, and never re-measures — which renders as
      // a few black pixels where the editor should be. automaticLayout keeps it
      // watching the container, so it sizes itself when the pane is shown.
      automaticLayout:true,
    }).then(ed=>{
      if(cancelled){ try{ed.dispose();}catch(_){} return; }
      edRef.current=ed;
      if(pendingRef.current!=null){ ed.setValue(pendingRef.current); valueRef.current=pendingRef.current; pendingRef.current=null; }
      ed.onDidChangeModelContent(()=>{ valueRef.current=ed.getValue(); markDirty(true); });
    }).catch(err=>setStatus({kind:'error',text:'Monaco failed to start: '+(err&&err.message||err)}));
    return()=>{ cancelled=true; try{edRef.current&&edRef.current.dispose();}catch(_){} edRef.current=null; };
  },[markDirty]);

  const setEditor=React.useCallback(text=>{
    valueRef.current=text;
    if(edRef.current){
      edRef.current.setValue(text);
      // The pane goes display:none -> block in the same commit that opens a
      // route. automaticLayout alone has proven unreliable across that
      // transition, so measure again once the browser has laid it out.
      requestAnimationFrame(()=>{ try{ edRef.current && edRef.current.layout(); }catch(_){} });
    }
    else { pendingRef.current=text; }
  },[]);

  const open=React.useCallback(async i=>{
    setOpenId(i.id);
    setStatus({kind:'busy',text:'Opening…'});
    try{
      const d=await API('GET','/api/integrations/source/'+encodeURIComponent(i.id));
      setClassName(d.builderClassName||'');
      setEditor(d.source||'');
      markDirty(false);
      setIsRunning(String(i.status||'').toUpperCase()==='ACTIVE');
      setStatus({kind:'idle'});
    }catch(e){
      if(!e.authRequired) setStatus({kind:'error',text:'Could not open: '+(e.message||e)});
    }
  },[setEditor,markDirty]);

  /* Save. The server compiles before it persists, so an uncompilable route is
     refused here and never reaches an ACTIVE integration — the same contract
     the YAML Designer gets from the YAML validator. Returns true on success so
     toggleRunning can decide whether to continue. */
  const save=React.useCallback(async()=>{
    if(!openId) return false;
    setStatus({kind:'busy',text:'Compiling…'});
    try{
      const headers=Object.assign({'Content-Type':'text/plain'}, await authHeaders());
      const r=await fetch('/api/integrations/source/'+encodeURIComponent(openId),
        {method:'PUT',headers,body:valueRef.current});
      if(r.status===401){ setStatus({kind:'error',text:'Session expired — sign in again.'}); return false; }
      const d=await r.json().catch(()=>({}));
      if(d.valid===false||d.saved===false){
        setStatus({kind:'error',text:'Not saved — '+(d.error||'the source did not compile')});
        return false;
      }
      markDirty(false);
      if(d.rebuildError){
        // Compiled and saved, but the live route did not come back. Say both —
        // "Saved" alone would imply the running route matches the editor.
        setStatus({kind:'error',text:'Saved, but the live route failed to restart: '+d.rebuildError});
      }else{
        setStatus({kind:'ok',text:d.hotReloaded?'Saved — live route reloaded':'Saved to Axon'});
      }
      if(d.integration) setIsRunning(String(d.integration.status||'').toUpperCase()==='ACTIVE');
      setIntegrations(prev=>prev?prev.map(p=>p.id===openId?Object.assign({},p,d.integration||{}):p):prev);
      return true;
    }catch(e){
      setStatus({kind:'error',text:String(e.message||e)});
      return false;
    }
  },[openId,markDirty]);

  /* Start/stop without leaving the designer. Saving first when dirty is the
     point: the engine compiles from the stored source, so activating with
     unsaved edits would start the previous version while the editor shows the
     new one. */
  const toggleRunning=React.useCallback(async()=>{
    if(!openId) return;
    const target=!isRunning;
    if(target&&dirtyRef.current){
      const ok=await save();
      if(!ok) return; // save already explained why
    }
    setStatus({kind:'busy',text:target?'Activating…':'Stopping…'});
    try{
      await API('POST','/api/integrations/'+encodeURIComponent(openId)+'/'+(target?'activate':'deactivate'));
      setIsRunning(target);
      setStatus({kind:'ok',text:target?'Route active':'Route stopped'});
    }catch(e){
      // Camel names the step and the unresolved property — far more useful than
      // "could not activate". Usually an unset connection attribute.
      if(!e.authRequired) setStatus({kind:'error',text:String(e.message||e)});
    }
  },[openId,isRunning,save]);

  const all=integrations||[];
  const editable=all.filter(isJavaRoute);
  const yamlOnly=all.length-editable.length;
  const current=editable.find(i=>i.id===openId);

  /* Several integrations can share one builderClassName (TaxlabAckRoutes backs
     three). Source is stored per integration, so editing here gives THIS
     integration its own compiled copy and leaves the others on the precompiled
     class — they diverge silently otherwise. */
  const sharing=current?editable.filter(i=>i.builderClassName===current.builderClassName&&i.id!==current.id):[];

  return (
    <div className="jd-wrap">
      <aside className="jd-aside">
        <div className="jd-aside__title">Java routes in Axon</div>
        <div className="jd-aside__list">
          {integrations===null&&<div className="jd-empty">Loading…</div>}
          {integrations!==null&&editable.length===0&&(
            <div className="jd-empty">
              No Java DSL routes.
              {yamlOnly>0&&<> {yamlOnly} integration{yamlOnly===1?' is':'s are'} YAML — design {yamlOnly===1?'it':'them'} in the YAML Designer.</>}
            </div>
          )}
          {editable.map(i=>(
            <button key={i.id} onClick={()=>open(i)} className="jd-route" aria-current={i.id===openId?'true':undefined}>
              <div className="jd-route__name">{i.name||i.id}</div>
              <div className="jd-route__meta">{i.status||'—'}</div>
            </button>
          ))}
        </div>
        {integrations!==null&&editable.length>0&&yamlOnly>0&&(
          <div className="jd-aside__foot">{yamlOnly} YAML route{yamlOnly===1?'':'s'} live in the YAML Designer.</div>
        )}
      </aside>

      <main className="jd-main">
        <div className="jd-header">
          <div className="jd-header__title">
            {current?(current.name||current.id):'Select a route'}
            {openId&&<span className={'jd-badge'+(isRunning?' jd-badge--on':'')}>{isRunning?'ACTIVE':'STOPPED'}</span>}
            {dirty&&<span className="jd-dirty"> · unsaved changes</span>}
          </div>
          {status.text&&<span className={'jd-status jd-status--'+status.kind}>{status.text}</span>}
          {openId&&(
            <div className="jd-actions">
              <button className="btn btn-sm btn-ghost" onClick={toggleRunning} disabled={status.kind==='busy'}>
                {isRunning?'Stop':'Activate'}
              </button>
              <button className="btn btn-sm btn-primary" onClick={save} disabled={status.kind==='busy'||!dirty}>
                Save
              </button>
            </div>
          )}
        </div>

        {openId&&className&&(
          <div className="jd-subhead">
            <code>{className}</code>
            {sharing.length>0&&(
              <span className="jd-share">
                · shared with {sharing.length} other integration{sharing.length===1?'':'s'} — saving here
                gives this one its own copy and leaves {sharing.length===1?'it':'them'} on the packaged class
              </span>
            )}
          </div>
        )}

        <div className="jd-editor" style={{display:openId?'block':'none'}}><div ref={hostRef} className="jd-monaco"/></div>
        {!openId&&(
          <div className="jd-placeholder">
            Pick a route on the left to edit its Camel Java DSL.
            <div className="jd-placeholder__sub">Saving compiles first — a route that does not compile is never persisted.</div>
          </div>
        )}
      </main>
    </div>
  );
}
